import type { CompanyFilters } from "../types";
import type { OpportunityGeneratorFilter } from "@/generated/company-search";
import {
  buildContactFiltersForExport,
  contactFiltersNarrowCompanies,
} from "./buildContactFilters";

/**
 * Strips UI-only fields from filters before sending to the API.
 * Segments persists metadata the Company Search GraphQL input doesn't accept:
 * - `intent.collectionId` (a UI reference)
 * - `modelScoreFilterUi` (V2 Model Score filter carrier — sent as the top-level
 *   `modelScoreFilter` arg instead; see modules/modelScore.tsx)
 * - `contactFilters` is rebuilt rather than stripped: in "Filter company list"
 *   mode (OppGen's default) the backend shape narrows the search to companies
 *   with matching contacts (company-search-api `contactFilters` input, HIP-12750);
 *   in "Export contacts only" mode it only reaches the export
 * - `companyConfigUi` (Tier/RGIF settings — sent as top-level
 *   isPlatformPermissible/isV1Dataset/rgifExclusiveCompany params instead;
 *   see modules/companyConfig.tsx)
 * - `companyMatchUi` (Tier/RGIF flags of the uploaded file — export-only,
 *   merged into the export's `companyMatch`)
 * - `scoringSourceIds` (the active scoring selection a segment was saved with;
 *   applied to the scoring store on hydrate, never a search filter — RGI-2264)
 */
export function stripIntentUiFields(
  filters: CompanyFilters | undefined
): OpportunityGeneratorFilter | undefined {
  if (!filters) return undefined;

  const {
    modelScoreFilterUi: _modelScoreFilterUi,
    scoringSourceIds: _scoringSourceIds,
    contactFilters,
    companyConfigUi: _companyConfigUi,
    companyMatchUi: _companyMatchUi,
    ...withoutUiFields
  } = filters;
  const searchContactFilters = contactFiltersNarrowCompanies(contactFilters)
    ? buildContactFiltersForExport(contactFilters)
    : undefined;
  const rest = searchContactFilters
    ? { ...withoutUiFields, contactFilters: searchContactFilters }
    : withoutUiFields;
  const { intent } = rest;

  if (!intent?.collectionId) {
    return { ...rest, intent } as OpportunityGeneratorFilter;
  }

  const { collectionId: _collectionId, ...intentRest } = intent;
  return { ...rest, intent: intentRest } as OpportunityGeneratorFilter;
}
